'use client'

import { useState, useCallback } from 'react';
import styles from '../styles/VerifyEmail.module.css'; // Reuse existing styles for now
import { FaEye, FaEyeSlash } from 'react-icons/fa';

interface CardFlowEmailVerificationFormProps {
  onVerificationSuccess: (data: { userId: string; email: string; isNewUser: boolean }) => void;
  onBack?: () => void; // Optional: if there's a step before this (e.g. payment method selection)
  // It might be better for CardPaymentFlow to control the back button that goes to payment selection
}

export const CardFlowEmailVerificationForm = ({ 
    onVerificationSuccess,
    onBack
}: CardFlowEmailVerificationFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [currentView, setCurrentView] = useState<'enterEmailPassword' | 'enterCode' | 'verified'>('enterEmailPassword');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying' | 'verified' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInitiateVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setStatus('error');
      return;
    }
    if (!email || !password) {
      setErrorMessage('Please enter email and password.');
      setStatus('error');
      return;
    }

    setIsLoading(true);
    setStatus('sending');
    setErrorMessage(null);

    try {
      const response = await fetch('/api/presale/verify-email/initiate-card-flow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send verification code.');
      }
      setStatus('sent');
      setCurrentView('enterCode');
    } catch (err: any) {
      setErrorMessage(err.message || 'Could not send verification code.');
      setStatus('error');
    }
    setIsLoading(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode) {
        setErrorMessage('Please enter the verification code.');
        setStatus('error');
        return;
    }
    setIsLoading(true);
    setStatus('verifying');
    setErrorMessage(null);

    try {
      const response = await fetch('/api/presale/verify-email/verify-card-flow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to verify code or create account.');
      }
      setStatus('verified');
      setCurrentView('verified');
      onVerificationSuccess({ userId: data.userId, email: data.email, isNewUser: data.isNewUser });
    } catch (err: any) {
      setErrorMessage(err.message || 'Could not verify code.');
      setStatus('error');
    }
    setIsLoading(false);
  };

  const handleResendCode = async () => {
    // Re-call initiate, but password is not needed here, only email for resend.
    // The initiate-card-flow route *requires* password for initial hash storage.
    // For a simple resend, we need to ensure the `initiate-card-flow` logic can handle a resend request for an existing email
    // without necessarily needing the password again (or it uses the previously stored hash).
    // The current `initiate-card-flow` *always* hashes and upserts password, so it should be fine.
    setIsLoading(true);
    setStatus('sending');
    setErrorMessage(null);
    try {
      const response = await fetch('/api/presale/verify-email/initiate-card-flow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }), // Send original password again to re-hash or match
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to resend verification code.');
      }
      setStatus('sent'); // Stay on enterCode view
      setVerificationCode(''); // Clear old code
      alert('A new verification code has been sent to your email.');
    } catch (err: any) {
      setErrorMessage(err.message || 'Could not resend verification code.');
      setStatus('error');
    }
    setIsLoading(false);
  };
  
  // Back button for the code entry screen to go back to email/password input
  const handleBackToEmailPassword = () => {
    setCurrentView('enterEmailPassword');
    setStatus('idle');
    setErrorMessage(null);
    setVerificationCode('');
  }

  if (currentView === 'verified') {
    return (
      <div className={styles.emailStep}> {/* Assuming styles.emailStep can be used */} 
        <div className={styles.emailVerified}> {/* Assuming styles.emailVerified can be used */} 
          <div className={styles.icon}>✓</div>
          <h3>Account Created & Email Verified!</h3>
          <p>Your account for <strong>{email}</strong> is ready.</p>
          {/* The parent CardPaymentFlow will handle the next step button via onVerificationSuccess */}
        </div>
      </div>
    );
  }

  if (currentView === 'enterCode') {
    return (
      <div className={styles.emailStep}>
        <h3>Enter Verification Code</h3>
        <p>A 6-digit code was sent to <strong>{email}</strong>. Please enter it below.</p>
        <form onSubmit={handleVerifyCode}>
          <div className={styles.formGroup}>
            <label htmlFor="cardVerificationCode">Verification Code</label>
            <input
              type="text"
              id="cardVerificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
              placeholder="123456"
              maxLength={6}
              required
              pattern="[0-9]{6}"
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={isLoading}
            />
          </div>
          {errorMessage && status === 'error' && <div className={styles.error}>{errorMessage}</div>}
          <button type="submit" className={styles.button} disabled={isLoading || verificationCode.length !== 6}>
            {isLoading && status === 'verifying' ? 'Verifying...' : 'Verify & Create Account'}
          </button>
          <div className={styles.formActionsRow}>
            <button type="button" onClick={handleBackToEmailPassword} className={styles.editButton} disabled={isLoading}>
              Edit Email/Password
            </button>
            <button type="button" onClick={handleResendCode} className={styles.editButton} disabled={isLoading && status ==='sending'}>
              {isLoading && status ==='sending' ? 'Sending...': 'Resend Code'}
            </button>
          </div>
        </form>
        {onBack && (
          <button onClick={onBack} className={styles.backButton} disabled={isLoading}>
            &larr; Back to Payment Methods
          </button>
        )}
      </div>
    );
  }

  // enterEmailPassword view
  return (
    <div className={styles.emailStep}>
      <h3>Create Account for Card Payment</h3>
      <p>Verify your email to proceed. Your password must be at least 8 characters.</p>
      <form onSubmit={handleInitiateVerification}>
        <div className={styles.formGroup}>
          <label htmlFor="cardEmail">Email Address</label>
          <input
            type="email"
            id="cardEmail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            disabled={isLoading}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="cardPassword">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? "text" : "password"}
              id="cardPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
              minLength={8}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={styles.passwordToggle}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="cardConfirmPassword">Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="cardConfirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="********"
              required
              minLength={8}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={styles.passwordToggle}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        {errorMessage && status === 'error' && <div className={styles.error}>{errorMessage}</div>}
        <button type="submit" className={styles.button} disabled={isLoading}>
          {isLoading && status === 'sending' ? 'Sending Code...' : 'Send Verification Code'}
        </button>
      </form>
      {onBack && (
        <button onClick={onBack} className={styles.backButton} disabled={isLoading}>
          &larr; Back to Payment Methods
        </button>
      )}
    </div>
  );
}; 